import { pb, escapeFilterValue } from '../lib/pocketbase'
import type { Activity, EntityType } from '../types'
import { ensureUserAgency } from '../utils/ensureAgency'
import { touchedBy, touchTargetFor, isNewerTouch, pbTimestamp } from '../shared/contactFreshness'
import type { KeyFact } from '../shared/captureFacts'

// Activity types matching PocketBase schema
export type ActivityType =
  | 'call'
  | 'email'
  | 'meeting'
  | 'text'
  | 'note'
  | 'deal_created'
  | 'deal_updated'
  | 'deal_stage_changed'
  | 'contract_created'
  | 'contract_signed'
  | 'payment_received'
  | 'task_completed'
  | 'client_added'
  | 'document_uploaded'
  | 'other'

// Re-export EntityType for convenience
export type { EntityType }

// Outcome options from PocketBase schema
export type ActivityOutcome = 'positive' | 'neutral' | 'negative'

export type ActivityPayload = {
  activity_type: ActivityType | string
  title: string
  description?: string | null
  entity_type?: EntityType | string | null
  entity_id?: string | null       // generic entity link (used for prospects, etc.)
  client_id?: string | null
  deal_id?: string | null
  contact_id?: string | null
  contract_id?: string | null
  task_id?: string | null
  invoice_id?: string | null
  metadata?: Record<string, any> | null
  outcome?: ActivityOutcome | null
  duration_minutes?: number | null
  agency_id?: string
  created_by?: string
  /** How it arrived: typed in the app, logged by an assistant, or captured from a recording or a text thread. */
  source?: 'manual' | 'mcp' | 'plaud' | 'imessage'
  source_url?: string | null
  source_id?: string | null
  key_facts?: KeyFact[] | null
  /** When it happened, in PocketBase's timestamp shape. Omitted: now. */
  occurred_at?: string
}

export type ActivityFilters = {
  entity_type?: EntityType | string
  entity_id?: string              // generic entity link
  client_id?: string
  deal_id?: string
  contact_id?: string
  contract_id?: string
  task_id?: string
  activity_type?: ActivityType | string
  outcome?: ActivityOutcome
  start_date?: string
  end_date?: string
  limit?: number
}

function mapActivity(record: Record<string, any>): Activity {
  return {
    id: record.id,
    activity_type: record.activity_type ?? '',
    title: record.title ?? '',
    description: record.description ?? null,
    entity_type: record.entity_type ?? null,
    client_id: record.client_id ?? null,
    deal_id: record.deal_id ?? null,
    contact_id: record.contact_id ?? null,
    contract_id: record.contract_id ?? null,
    task_id: record.task_id ?? null,
    invoice_id: record.invoice_id ?? null,
    metadata: record.metadata ?? null,
    outcome: record.outcome ?? null,
    duration_minutes: record.duration_minutes ?? null,
    created_by: record.created_by ?? '',
    agency_id: record.agency_id ?? '',
    created: record.created ?? '',
    updated: record.updated ?? '',
    // PocketBase returns '' for an unset date or select; that is unknown.
    occurred_at: record.occurred_at || null,
    source: record.source || null,
    source_id: record.source_id || null,
    source_url: record.source_url || null,
    key_facts: record.key_facts ?? null,
    // Include expanded relations if available
    expand: record.expand ?? null,
  } as Activity
}

export async function listActivities(filters?: ActivityFilters) {
  
  // Get current user's agency for filtering
  const user = pb.authStore.model
  const agencyId = (user as any)?.agency_id

  const filterParts: string[] = []

  // Always filter by agency for data isolation
  if (agencyId) {
    filterParts.push(`agency_id = "${escapeFilterValue(agencyId)}"`)
  }

  if (filters?.entity_type) {
    filterParts.push(`entity_type = "${escapeFilterValue(filters.entity_type)}"`)
  }
  if (filters?.entity_id) {
    filterParts.push(`entity_id = "${escapeFilterValue(filters.entity_id)}"`)
  }
  if (filters?.client_id) {
    filterParts.push(`client_id = "${escapeFilterValue(filters.client_id)}"`)
  }
  if (filters?.deal_id) {
    filterParts.push(`deal_id = "${escapeFilterValue(filters.deal_id)}"`)
  }
  if (filters?.contact_id) {
    filterParts.push(`contact_id = "${escapeFilterValue(filters.contact_id)}"`)
  }
  if (filters?.contract_id) {
    filterParts.push(`contract_id = "${escapeFilterValue(filters.contract_id)}"`)
  }
  if (filters?.task_id) {
    filterParts.push(`task_id = "${escapeFilterValue(filters.task_id)}"`)
  }
  if (filters?.activity_type) {
    filterParts.push(`activity_type = "${escapeFilterValue(filters.activity_type)}"`)
  }
  if (filters?.outcome) {
    filterParts.push(`outcome = "${escapeFilterValue(filters.outcome)}"`)
  }
  if (filters?.start_date) {
    filterParts.push(`created >= "${escapeFilterValue(filters.start_date)}"`)
  }
  if (filters?.end_date) {
    filterParts.push(`created <= "${escapeFilterValue(filters.end_date)}"`)
  }

  const filter = filterParts.length > 0 ? filterParts.join(' && ') : ''
  const options: any = {
    sort: '-created',
    expand: 'created_by,client_id,deal_id,contact_id,contract_id,task_id,invoice_id',
  }

  if (filter) {
    options.filter = filter
  }

  try {
    let data: Record<string, any>[]
    if (filters?.limit) {
      const result = await pb.collection('activities').getList<Record<string, any>>(1, filters.limit, options)
      data = result.items
    } else {
      data = await pb.collection('activities').getFullList<Record<string, any>>(options)
    }
    return data.map(mapActivity)
  } catch (error: any) {
    throw new Error(`Failed to load activities: ${error.message}`)
  }
}

export async function getActivity(activityId: string) {
  try {
    const record = await pb.collection('activities').getOne<Record<string, any>>(activityId, {
      expand: 'created_by,client_id,deal_id,contact_id,contract_id,task_id,invoice_id',
    })
    return mapActivity(record)
  } catch (error: any) {
    throw new Error(`Failed to load activity: ${error.message}`)
  }
}

export async function createActivity(payload: ActivityPayload) {
  try {
    // Get agency ID if not provided
    let agencyId = payload.agency_id
    if (!agencyId) {
      agencyId = await ensureUserAgency()
    }

    // Get current user ID if not provided
    let createdBy = payload.created_by
    if (!createdBy) {
      const user = pb.authStore.model
      createdBy = user?.id || ''
    }

    const createPayload: Record<string, any> = {
      activity_type: payload.activity_type,
      title: payload.title,
      agency_id: agencyId,
      created_by: createdBy,
    }

    // Add optional fields only if they have values
    if (payload.description) createPayload.description = payload.description
    if (payload.entity_type) createPayload.entity_type = payload.entity_type
    if (payload.entity_id) createPayload.entity_id = payload.entity_id
    if (payload.client_id) createPayload.client_id = payload.client_id
    if (payload.deal_id) createPayload.deal_id = payload.deal_id
    if (payload.contact_id) createPayload.contact_id = payload.contact_id
    if (payload.contract_id) createPayload.contract_id = payload.contract_id
    if (payload.task_id) createPayload.task_id = payload.task_id
    if (payload.invoice_id) createPayload.invoice_id = payload.invoice_id
    if (payload.metadata) createPayload.metadata = payload.metadata
    if (payload.outcome) createPayload.outcome = payload.outcome
    if (payload.duration_minutes != null) createPayload.duration_minutes = payload.duration_minutes
    if (payload.source) createPayload.source = payload.source
    if (payload.source_url) createPayload.source_url = payload.source_url
    if (payload.source_id) createPayload.source_id = payload.source_id
    if (payload.key_facts && payload.key_facts.length) createPayload.key_facts = payload.key_facts
    // When it happened. Something being recorded as it happens happened now;
    // a captured conversation brings its own time, so a text from March
    // never reads as today.
    createPayload.occurred_at = payload.occurred_at ?? pbTimestamp()

    const record = await pb.collection('activities').create(createPayload)
    return mapActivity(record)
  } catch (error: any) {
    throw new Error(`Failed to create activity: ${error.message}`)
  }
}

export async function deleteActivity(activityId: string) {
  await pb.collection('activities').delete(activityId)
}

// Convenience functions for fetching activities by entity

export async function getClientActivities(clientId: string, limit?: number) {
  return listActivities({ client_id: clientId, limit })
}

export async function getDealActivities(dealId: string, limit?: number) {
  return listActivities({ deal_id: dealId, limit })
}

export async function getContactActivities(contactId: string, limit?: number) {
  return listActivities({ contact_id: contactId, limit })
}

export async function getContractActivities(contractId: string, limit?: number) {
  return listActivities({ contract_id: contractId, limit })
}

export async function getTaskActivities(taskId: string, limit?: number) {
  return listActivities({ task_id: taskId, limit })
}

export async function getGlobalActivities(limit: number = 50) {
  return listActivities({ limit })
}

export async function getRecentActivities(limit: number = 20) {
  const yesterday = new Date()
  yesterday.setDate(yesterday.getDate() - 1)
  return listActivities({
    start_date: yesterday.toISOString(),
    limit,
  })
}

// Helper functions to log specific activity types

/**
 * The activity landed; stamping the person's last-contact date did not.
 *
 * Carries the saved activity so a caller can say exactly that — "logged, but
 * the date did not update" — rather than reporting a failed log, which gets
 * the same conversation logged twice.
 */
export class TouchNotStampedError extends Error {
  activity: ReturnType<typeof mapActivity>
  constructor(message: string, activity: ReturnType<typeof mapActivity>) {
    super(message)
    this.name = 'TouchNotStampedError'
    this.activity = activity
  }
}

/**
 * Warm the person a conversation was with. One rule for every path — which
 * field, what format — from shared/contactFreshness, and never backwards: a
 * conversation logged late must not make someone look less recently
 * contacted than they were.
 */
async function stampTouch(activity: { client_id?: string | null; contact_id?: string | null; entity_type?: string | null; entity_id?: string | null }, at: Date = new Date()) {
  const who = touchedBy(activity)
  if (!who) return
  const target = touchTargetFor(who.entityType, who.entityId, at)
  if (!target) return
  const current = await pb.collection(target.collection).getOne(target.id, { fields: target.field })
  if (!isNewerTouch((current as Record<string, any>)[target.field], at)) return
  await pb.collection(target.collection).update(target.id, { [target.field]: target.value })
}

type ConversationOptions = {
  title: string
  description?: string
  client_id?: string
  deal_id?: string
  contact_id?: string
  entity_type?: string
  entity_id?: string
  duration_minutes?: number
  outcome?: ActivityOutcome
  metadata?: Record<string, any>
  /** When it happened. Omitted: now — the moment it is being logged. */
  at?: Date
}

/**
 * A conversation: saved as an activity, then the person is stamped. Until
 * this, logCall / logEmail / logMeeting stamped nothing, and each form that
 * used them stamped on its own, by its own rules.
 */
async function logConversation(activityType: 'call' | 'email' | 'meeting' | 'text', { at: when, ...options }: ConversationOptions) {
  const at = when ?? new Date()
  if (at.getTime() > Date.now() + 5 * 60 * 1000) {
    throw new Error('A conversation cannot be logged for a time that has not happened yet.')
  }
  const activity = await createActivity({
    activity_type: activityType,
    entity_type: options.client_id ? 'client' : options.deal_id ? 'deal' : 'contact',
    ...options,
    source: 'manual',
    occurred_at: pbTimestamp(at),
  })
  try {
    await stampTouch(options, at)
  } catch (err: any) {
    throw new TouchNotStampedError(`Logged, but the last-contact date did not update: ${err?.message ?? 'unknown error'}`, activity)
  }
  return activity
}

export async function logCall(options: ConversationOptions) {
  return logConversation('call', options)
}

export async function logEmail(options: ConversationOptions) {
  return logConversation('email', options)
}

export async function logMeeting(options: ConversationOptions) {
  return logConversation('meeting', options)
}

/**
 * A text message: a conversation like any other, and it warms the person.
 * Texts are how most athletes are actually reached.
 */
export async function logText(options: ConversationOptions) {
  return logConversation('text', options)
}

export async function logNote(options: {
  title: string
  description?: string
  client_id?: string
  deal_id?: string
  contact_id?: string
  contract_id?: string
  task_id?: string
  metadata?: Record<string, any>
  occurred_at?: string
}) {
  // Determine entity type based on which ID is provided
  let entityType: EntityType = 'client'
  if (options.deal_id) entityType = 'deal'
  else if (options.contact_id) entityType = 'contact'
  else if (options.contract_id) entityType = 'contract'
  else if (options.task_id) entityType = 'task'

  return createActivity({
    activity_type: 'note',
    entity_type: entityType,
    ...options,
    source: 'manual',
  })
}

export async function logDealUpdate(options: {
  title: string
  description?: string
  deal_id: string
  client_id?: string
  metadata?: Record<string, any>
}) {
  return createActivity({
    activity_type: 'deal_updated',
    entity_type: 'deal',
    ...options,
  })
}

export async function logContractUpdate(options: {
  title: string
  description?: string
  contract_id: string
  client_id?: string
  metadata?: Record<string, any>
}) {
  return createActivity({
    activity_type: 'contract_signed',
    entity_type: 'contract',
    ...options,
  })
}

export async function logStatusChange(options: {
  title: string
  description?: string
  entity_type: EntityType
  client_id?: string
  deal_id?: string
  contract_id?: string
  task_id?: string
  metadata?: Record<string, any>
}) {
  return createActivity({
    activity_type: 'deal_stage_changed',
    ...options,
  })
}

// Additional helper functions for new activity types

export async function logDealCreated(options: {
  title: string
  description?: string
  deal_id: string
  client_id?: string
  metadata?: Record<string, any>
}) {
  return createActivity({
    activity_type: 'deal_created',
    entity_type: 'deal',
    ...options,
  })
}

export async function logContractCreated(options: {
  title: string
  description?: string
  contract_id: string
  client_id?: string
  metadata?: Record<string, any>
}) {
  return createActivity({
    activity_type: 'contract_created',
    entity_type: 'contract',
    ...options,
  })
}

export async function logPaymentReceived(options: {
  title: string
  description?: string
  invoice_id?: string
  client_id?: string
  metadata?: Record<string, any>
}) {
  return createActivity({
    activity_type: 'payment_received',
    entity_type: 'invoice',
    ...options,
  })
}

export async function logTaskCompleted(options: {
  title: string
  description?: string
  task_id: string
  client_id?: string
  deal_id?: string
  metadata?: Record<string, any>
}) {
  return createActivity({
    activity_type: 'task_completed',
    entity_type: 'task',
    ...options,
  })
}

export async function logClientAdded(options: {
  title: string
  description?: string
  client_id: string
  metadata?: Record<string, any>
}) {
  return createActivity({
    activity_type: 'client_added',
    entity_type: 'client',
    ...options,
  })
}

export async function logDocumentUploaded(options: {
  title: string
  description?: string
  client_id?: string
  deal_id?: string
  contract_id?: string
  metadata?: Record<string, any>
}) {
  return createActivity({
    activity_type: 'document_uploaded',
    entity_type: options.client_id ? 'client' : options.deal_id ? 'deal' : 'contract',
    ...options,
  })
}
